import React, { FC, useState, useRef } from "react";
import Seo from "components/Seo/Seo";
import { Link } from "react-router-dom";
import { useForm, useAuthStore } from "hooks";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

export interface PageSignUpProps {
  className?: string;
}

const registerFormFields = {
  registerName: "",
  registerEmail: "",
  registerPhone: "",
  registerAddress: "",
  registerDateBirth: "",
  registerPassword: "",
  registerPassword2: "",
};

const inputDark =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-slate-500 transition focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/30";
const labelDark = "mb-1.5 block text-sm font-medium text-slate-300";
const btnGradient =
  "inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 px-7 py-3 text-base font-bold uppercase tracking-wide text-white shadow-lg shadow-violet-600/30 transition-all duration-200 hover:from-violet-500 hover:to-fuchsia-500 hover:shadow-violet-500/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 active:scale-95 disabled:opacity-70";

const PageSignUp: FC<PageSignUpProps> = ({ className = "" }) => {
  const { registerName, registerEmail, registerPhone, registerPassword, registerPassword2, onInputChange } = useForm(registerFormFields);
  const { startRegister, requestPasswordReser } = useAuthStore();
  const [error, setError] = useState("");
  const [emailExists, setEmailExists] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  // Anti-spam: honeypot (campo señuelo oculto que solo rellenan los bots) y marca de tiempo
  // de apertura del formulario (para detectar envíos instantáneos automatizados).
  const [website, setWebsite] = useState("");
  const formLoadedAt = useRef(Date.now());

  const handleForgotPassword = async () => {
    setResetLoading(true);
    await requestPasswordReser({ email: registerEmail });
    setResetLoading(false);
    setResetSent(true);
  };

  const handleRegister = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nameRegex = /^[a-zA-Z\s]+$/;
    try {
      if (!registerName && !registerEmail && !registerPhone) {
        setError("Todos los campos marcados con (*) son obligatorios");
        return;
      }
      if (!registerName) {
        setError("Debes completar el campo nombre");
        return;
      }
      if (!nameRegex.test(registerName)) {
        setError("El nombre no puede contener números ni símbolos como @");
        return;
      }
      if (!registerEmail) {
        setError("Debes completar el campo Email");
        return;
      }
      if (!registerPhone) {
        setError("Debes completar el campo Movil");
        return;
      }
      if (registerPassword !== registerPassword2) {
        setError("Las contraseñas no coinciden");
        return;
      }
      setError("");
      setEmailExists(false);
      setResetSent(false);
      const result = await startRegister({ name: registerName, email: registerEmail, phone: registerPhone, password: registerPassword, website, elapsedMs: Date.now() - formLoadedAt.current });
      if (result && !result.ok) {
        setError(result.error);
        setEmailExists(!!result.emailExists);
      }
    } catch (error) {
      setError("Error en el registro");
    }
  };

  return (
    <div className={`nc-PageSignUp relative min-h-screen overflow-hidden bg-gradient-to-b from-[#0c0a16] to-[#15101f] px-4 py-16 ${className}`} data-nc-id="PageSignUp">
      <Seo
        path="/signup"
        title="Crear Cuenta | Karaoke Rooms Barcelona"
        description="Regístrate en Karaoke Rooms Barcelona y reserva tu sala privada de karaoke en Barcelona en segundos."
        noindex
      />
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-0 h-80 w-80 -translate-x-1/2 rounded-full bg-violet-600/20 blur-[120px]" />
      </div>

      <div className="relative mx-auto max-w-md">
        <div className="flex flex-col items-center text-center">
          <h1 className="text-3xl font-bold uppercase tracking-wide text-white sm:text-4xl">Crear cuenta</h1>
          <div className="mt-3 h-1 w-20 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500" />
        </div>

        <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.04] p-6 shadow-2xl backdrop-blur-xl sm:p-8">
          <form className="grid grid-cols-1 gap-5" onSubmit={handleRegister}>
            {/* Honeypot anti-bot: invisible para humanos (fuera de pantalla, sin tab ni
                autocompletar). Si llega con valor, el backend descarta el registro. */}
            <input
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
            />
            <div>
              <label htmlFor="registerName" className={labelDark}>Nombre <span className="text-rose-400">*</span></label>
              <input id="registerName" type="text" placeholder="Tu nombre" name="registerName" value={registerName} onChange={onInputChange} className={inputDark} />
            </div>
            <div>
              <label htmlFor="registerEmail" className={labelDark}>Email <span className="text-rose-400">*</span></label>
              <input id="registerEmail" type="email" placeholder="ejemplo@ejemplo.com" name="registerEmail" value={registerEmail} onChange={onInputChange} className={inputDark} />
            </div>
            <div>
              <label className={labelDark}>Móvil <span className="text-rose-400">*</span></label>
              <PhoneInput
                country={"es"}
                placeholder="Ingrese su número"
                inputStyle={{
                  color: "#fff",
                  background: "rgba(255,255,255,0.05)",
                  borderRadius: "8px",
                  border: "1px solid rgba(255,255,255,0.1)",
                  width: "100%",
                  height: "48px",
                }}
                buttonStyle={{ background: "transparent", border: "none" }}
                dropdownStyle={{ background: "#1a1530", color: "#fff" }}
                onChange={(value) => onInputChange({ target: { name: "registerPhone", value } } as any)}
              />
            </div>
            <div>
              <label htmlFor="registerPassword" className={labelDark}>Contraseña</label>
              <input id="registerPassword" type="password" placeholder="••••••••" name="registerPassword" value={registerPassword} onChange={onInputChange} className={inputDark} />
            </div>
            <div>
              <label htmlFor="registerPassword2" className={labelDark}>Repetir contraseña</label>
              <input id="registerPassword2" type="password" placeholder="••••••••" name="registerPassword2" value={registerPassword2} onChange={onInputChange} className={inputDark} />
            </div>

            {error && (
              <p role="alert" className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-center text-sm text-rose-300">{error}</p>
            )}

            {emailExists && !resetSent && (
              <div className="rounded-lg border border-violet-500/30 bg-violet-500/10 px-4 py-3 text-center text-sm text-slate-300">
                <p>¿Es tuya esta cuenta?</p>
                <div className="mt-2 flex flex-col items-center justify-center gap-2 sm:flex-row">
                  <Link to="/login" className="font-semibold text-violet-300 hover:text-violet-200">Iniciar sesión</Link>
                  <span className="hidden text-slate-500 sm:inline">·</span>
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={resetLoading}
                    className="font-semibold text-violet-300 underline-offset-2 hover:text-violet-200 hover:underline disabled:opacity-60"
                  >
                    {resetLoading ? "Enviando…" : "¿Olvidaste tu contraseña?"}
                  </button>
                </div>
              </div>
            )}

            {resetSent && (
              <p className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-center text-sm text-emerald-300">
                Si el email está registrado, te hemos enviado un correo para restablecer la contraseña. Revisa también spam.
              </p>
            )}

            <button type="submit" className={btnGradient}>Registrarme</button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-400">
            ¿Ya tienes una cuenta?{" "}
            <Link to="/login" className="font-semibold text-violet-300 hover:text-violet-200">Acceder</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PageSignUp;
